import React, { useRef, useState, useEffect, useMemo } from 'react';
import maskImg from './imagemask.png';
import { 
  DEFAULT_CONTAINER_WIDTH, 
  DEFAULT_CONTAINER_HEIGHT, 
  DEFAULT_MASK_WIDTH, 
  DEFAULT_MASK_HEIGHT, 
  DEFAULT_MASK_SCALE, 
  MAX_STREAK,
  OPACITY_TRANSITION_DURATION 
} from '../../constants';

const DraggableMaskedImage = ({
  src,
  width = DEFAULT_CONTAINER_WIDTH,
  height = DEFAULT_CONTAINER_HEIGHT,
  startingX = 0,
  startingY = 0,
  maskWidth = DEFAULT_MASK_WIDTH,
  maskHeight = DEFAULT_MASK_HEIGHT,
  maskScale = DEFAULT_MASK_SCALE,
  showFullImage = false,
  draggable = true,
  grayscale = false,
  // onMaskDragEnd = () => {},
  onReady,
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: width, h: height });
  const [imgNatural, setImgNatural] = useState({ width: width, height: height });
  const [maskReady, setMaskReady] = useState(false);
  const [displayReady, setDisplayReady] = useState(false);

  useEffect(() => {
    if (!src) return;
    setMaskReady(false);
    setDisplayReady(false);
    // Reset drag offset when image changes to prevent position leakage
    setDragOffset({ x: 0, y: 0 });
    const img = new window.Image();
    img.onload = () => {
      setImgNatural({ width: img.naturalWidth, height: img.naturalHeight });
      setMaskReady(true);
    };
    img.src = src;
  }, [src]);

  // Observe container size for responsive behavior while preserving default dimensions
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const nextW = Math.round(rect.width) || width;
      const nextH = Math.round(rect.height) || height;
      setContainerSize({ w: nextW, h: nextH });
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(element);
    window.addEventListener('resize', updateSize);
    return () => {
      try { ro.disconnect(); } catch (_) {}
      window.removeEventListener('resize', updateSize);
    };
  }, [width, height]);

  // Calculate image display size and offset to fit in container (object-fit: contain)
  const { imgDisplayW, imgDisplayH, offsetX, offsetY } = useMemo(() => {
    const boxW = containerSize.w;
    const boxH = containerSize.h;
    const aspectImg = imgNatural.width / imgNatural.height;
    const aspectBox = boxW / boxH;
    let imgDisplayW, imgDisplayH, offsetX, offsetY;
    if (aspectImg > aspectBox) {
      // Image is wider than box
      imgDisplayW = boxW;
      imgDisplayH = boxW / aspectImg;
      offsetX = 0;
      offsetY = (boxH - imgDisplayH) / 2;
    } else {
      // Image is taller than box
      imgDisplayH = boxH;
      imgDisplayW = boxH * aspectImg;
      offsetY = 0;
      offsetX = (boxW - imgDisplayW) / 2;
    }
    return { imgDisplayW, imgDisplayH, offsetX, offsetY };
  }, [imgNatural, containerSize]);

  const scale = Math.max(0, Math.min(maskScale, MAX_STREAK));
  // Make mask size responsive to container size so visible area remains constant across widths
  const containerScale = useMemo(() => {
    const wRatio = (containerSize?.w || width) / width;
    const hRatio = (containerSize?.h || height) / height;
    return Math.min(wRatio, hRatio) || 1;
  }, [containerSize, width, height]);
  const baseMaskWResponsive = maskWidth * containerScale;
  const baseMaskHResponsive = maskHeight * containerScale;
  // Calculate mask size as responsiveBase * scale, but not exceeding image display size
  const scaledMaskW = Math.min(
    imgDisplayW,
    Math.round(baseMaskWResponsive * (scale / 100))
  );
  const scaledMaskH = Math.min(
    imgDisplayH,
    Math.round(baseMaskHResponsive * (scale / 100))
  );
  // Calculate center offset if maskScale > 100, using responsive baseline so position stays consistent across widths
  const baselineMaskW = Math.min(imgDisplayW, baseMaskWResponsive);
  const baselineMaskH = Math.min(imgDisplayH, baseMaskHResponsive);
  const centerOffsetX = scale > 100 ? -0.5 * (scaledMaskW - baselineMaskW) : 0;
  const centerOffsetY = scale > 100 ? -0.5 * (scaledMaskH - baselineMaskH) : 0;
  const zoomFactor = useMemo(
    () => Math.max(imgDisplayW / scaledMaskW || 1, imgDisplayH / scaledMaskH || 1),
    [imgDisplayW, imgDisplayH, scaledMaskW, scaledMaskH]
  );

  const clamp = (val, min, max) => Math.max(min, Math.min(val, max));
  // Map startingX/Y% to display image coordinates
  // const initialX = clamp(
  //   Math.round((startingX / 100) * imgDisplayW) + centerOffsetX,
  //   0,
  //   imgDisplayW - scaledMaskW
  // );
  // const initialY = clamp(
  //   Math.round((startingY / 100) * imgDisplayH) + centerOffsetY,
  //   0,
  //   imgDisplayH - scaledMaskH
  // );
  // Calculate base mask position on every render to prevent state leakage
  const baseMaskPos = useMemo(() => {
    const newX = clamp(
      Math.round((startingX / 100) * imgDisplayW) + centerOffsetX,
      0,
      imgDisplayW - scaledMaskW
    );
    const newY = clamp(
      Math.round((startingY / 100) * imgDisplayH) + centerOffsetY,
      0,
      imgDisplayH - scaledMaskH
    );
    return { x: newX, y: newY };
  }, [startingX, startingY, imgDisplayW, imgDisplayH, centerOffsetX, centerOffsetY, scaledMaskW, scaledMaskH]);

  // Reset drag offset when starting position changes (new image or mode switch)
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
  }, [startingX, startingY]);

  // Track drag offset separately to maintain dragging functionality
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  // Calculate final mask position including drag offset
  const maskPos = useMemo(() => ({
    x: baseMaskPos.x + dragOffset.x,
    y: baseMaskPos.y + dragOffset.y
  }), [baseMaskPos, dragOffset]);

  // Mouse event handlers
  const onMouseDown = (e) => {
    if (!draggable) return;
    e.preventDefault();
    setDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left - baseMaskPos.x - offsetX,
      y: e.clientY - rect.top - baseMaskPos.y - offsetY,
    });
  };

  const onMouseMove = (e) => {
    if (!dragging || !draggable) return;
    const rect = containerRef.current.getBoundingClientRect();
    let newX = e.clientX - rect.left - dragOffset.x;
    let newY = e.clientY - rect.top - dragOffset.y;
    // Clamp to image area
    newX = clamp(newX, offsetX, offsetX + imgDisplayW - scaledMaskW) - offsetX;
    newY = clamp(newY, offsetY, offsetY + imgDisplayH - scaledMaskH) - offsetY;
    setDragOffset({ x: newX - baseMaskPos.x, y: newY - baseMaskPos.y });
  };

  // const onMouseUp = () => {
  //   if (!draggable) return;
  //   setDragging(false);
  //   // Reverse-engineer starting X/Y percentages that would produce the current mask position
  //   const revX = ((maskPos.x - centerOffsetX) / imgDisplayW) * 100;
  //   const revY = ((maskPos.y - centerOffsetY) / imgDisplayH) * 100;
  //   console.log(`Reverse-engineered start%: ${revX.toFixed(2)}% ${revY.toFixed(2)}%`);
  //   try {
  //     onMaskDragEnd({ xPercent: revX, yPercent: revY });
  //   } catch (_) {}
  // };

  useEffect(() => {
    if (dragging && draggable) {
      window.addEventListener('mousemove', onMouseMove);
      // window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        // window.removeEventListener('mouseup', onMouseUp);
      };
    }
  });

  // Signal readiness once the image is loaded and layout values are computed
  useEffect(() => {
    if (showFullImage) {
      if (!displayReady) {
        setDisplayReady(true);
        try { onReady && onReady(); } catch (_) {}
      }
      return;
    }
    if (!maskReady) return;
    if (!imgDisplayW || !imgDisplayH || !scaledMaskW || !scaledMaskH) return;
    // Wait a frame to ensure DOM has applied measurements/positions
    let raf = requestAnimationFrame(() => {
      setDisplayReady(true);
      try { onReady && onReady(); } catch (_) {}
    });
    return () => {
      try { cancelAnimationFrame(raf); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskReady, imgDisplayW, imgDisplayH, scaledMaskW, scaledMaskH, showFullImage]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'black',
        userSelect: 'none',
        opacity: displayReady ? 1 : 0,
        transition: `opacity ${OPACITY_TRANSITION_DURATION}ms ease`,
        pointerEvents: displayReady ? 'auto' : 'none',
      }}
    >
      {showFullImage ? (
        <img
          src={src}
          alt="full"
          style={{
            width: imgDisplayW,
            height: imgDisplayH,
            display: 'block',
            objectFit: 'contain',
            position: 'absolute',
            left: offsetX,
            top: offsetY,
            filter: grayscale ? 'grayscale(1)' : 'none',
          }}
          draggable={false}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            WebkitMaskImage: `url(${maskImg})`,
            WebkitMaskSize: 'cover',
            maskImage: `url(${maskImg})`,
            maskSize: 'cover',
            cursor: draggable ? (dragging ? 'grabbing' : 'grab') : 'default',
            pointerEvents: draggable ? 'auto' : 'none',
          }}
          onMouseDown={onMouseDown}
        >
          {maskReady && (
            <img
              src={src}
              alt="masked"
              style={{
                position: 'absolute',
                width: imgDisplayW * zoomFactor,
                height: imgDisplayH * zoomFactor,
                left: containerSize.w / 2 - (offsetX + (maskPos.x + scaledMaskW / 2)) * zoomFactor,
                top: containerSize.h / 2 - (offsetY + (maskPos.y + scaledMaskH / 2)) * zoomFactor,
                objectFit: 'contain',
                pointerEvents: 'none',
                userSelect: 'none',
                filter: grayscale ? 'grayscale(1)' : 'none',
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DraggableMaskedImage;
